/**
 * Task Table Component
 * Sortable table displaying task list
 */

import { useState, useMemo } from 'react';
import { Badge, CoolingBadge } from '../ui';
import { ArrowUpDown, ArrowUp, ArrowDown, Package, Copy, Lock, Clock, Truck, CheckCircle, Loader2, UserX } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SLOT_STATE_LABELS, getSlotStateBadgeClasses } from '../../utils/pickupSlotStatus';
import type { RoutingTask } from '../../types';
import { useCan } from '../../contexts/useAuth';

type SortField = 'uid' | 'platform_code' | 'task_status' | 'merchant_name' | 'area_name' | 'province' | 'zone_name' | 'planned_route_name' | 'number_of_orders' | 'created_at';
type SortDirection = 'asc' | 'desc';

// Task statuses that have a driver assignment and can therefore be unassigned/released.
// (Hidden for 'new' — nothing assigned — and 'done'/'cancelled' — terminal.)
const ASSIGNED_TASK_STATUSES = ['assigned', 'driving', 'arrived', 'in-progress', 'stopped'];

interface TaskTableProps {
  tasks: RoutingTask[];
  selectedTasks: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  onTaskClick?: (task: RoutingTask) => void;
  isLoading?: boolean;
  onMarkTaskAsDone?: (taskUid: string, merchantName?: string) => void;
  isMarkingDone?: string | null;
  onUnassignTask?: (taskUid: string, merchantName?: string) => void;
  isUnassigning?: string | null;
}

interface SortState {
  field: SortField;
  direction: SortDirection;
}

function SortIcon({ field, sort }: { field: SortField; sort: SortState }) {
  if (sort.field !== field) {
    return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
  }
  return sort.direction === 'asc' ? (
    <ArrowUp className="h-4 w-4 text-blue-600" />
  ) : (
    <ArrowDown className="h-4 w-4 text-blue-600" />
  );
}

export default function TaskTable({
  tasks,
  selectedTasks,
  onSelectionChange,
  onTaskClick,
  isLoading,
  onMarkTaskAsDone,
  isMarkingDone,
  onUnassignTask,
  isUnassigning,
}: TaskTableProps) {
  const can = useCan();
  const [sort, setSort] = useState<SortState>({
    field: 'uid',
    direction: 'asc',
  });

  const handleSort = (field: SortField) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      let aVal = a[sort.field] ?? '';
      let bVal = b[sort.field] ?? '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tasks, sort]);

  // Filter out planned tasks for selection
  const selectableTasks = useMemo(() => tasks.filter(t => !t.is_planned), [tasks]);

  const handleSelectAll = () => {
    if (selectedTasks.size === selectableTasks.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableTasks.map((t) => t.uid)));
    }
  };

  const handleSelectTask = (taskUid: string, isPlanned: boolean) => {
    // Don't allow selection of planned tasks
    if (isPlanned) return;
    
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskUid)) {
      newSelected.delete(taskUid);
    } else {
      newSelected.add(taskUid);
    }
    onSelectionChange(newSelected);
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 border-b border-gray-200" />
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-14 border-b border-gray-100 flex items-center px-4 gap-4">
              <div className="w-4 h-4 bg-gray-200 rounded" />
              <div className="w-24 h-4 bg-gray-200 rounded" />
              <div className="w-16 h-4 bg-gray-200 rounded" />
              <div className="flex-1 h-4 bg-gray-200 rounded" />
              <div className="w-12 h-4 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
        <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No tasks found</h3>
        <p className="text-gray-500">
          Try adjusting your filters or select a different date.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-3 py-3">
                <input
                  type="checkbox"
                  checked={selectedTasks.size === selectableTasks.length && selectableTasks.length > 0}
                  onChange={handleSelectAll}
                  disabled={selectableTasks.length === 0}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  title={selectableTasks.length === 0 ? 'No selectable tasks' : `Select all ${selectableTasks.length} available tasks`}
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('merchant_name')}
              >
                <div className="flex items-center gap-1">
                  Merchant
                  <SortIcon field="merchant_name" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('area_name')}
              >
                <div className="flex items-center gap-1">
                  Area
                  <SortIcon field="area_name" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Task&nbsp;Date
                  <SortIcon field="created_at" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('task_status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  <SortIcon field="task_status" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('zone_name')}
              >
                <div className="flex items-center gap-1">
                  Zone
                  <SortIcon field="zone_name" sort={sort} />
                </div>
              </th>
              {/*
                Which route will actually collect this. The zone alone could not
                answer it — one zone's work is split across a shift route and a
                temperature-control route, and ops had no way to tell which
                (Ali, 2026-08-18: "we cannot identify this task is in which route").
              */}
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('planned_route_name')}
              >
                <div className="flex items-center gap-1">
                  Route
                  <SortIcon field="planned_route_name" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('number_of_orders')}
              >
                <div className="flex items-center gap-1">
                  Orders
                  <SortIcon field="number_of_orders" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('uid')}
              >
                <div className="flex items-center gap-1">
                  UID
                  <SortIcon field="uid" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('platform_code')}
              >
                <div className="flex items-center gap-1">
                  Platform
                  <SortIcon field="platform_code" sort={sort} />
                </div>
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('province')}
              >
                <div className="flex items-center gap-1">
                  Province
                  <SortIcon field="province" sort={sort} />
                </div>
              </th>
              {(onMarkTaskAsDone || onUnassignTask) && (
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedTasks.map((task) => (
              <tr
                key={task.uid}
                className={cn(
                  'hover:bg-gray-50 cursor-pointer transition-colors',
                  selectedTasks.has(task.uid) && 'bg-blue-50',
                  task.is_planned && 'bg-amber-50/50'
                )}
                onClick={() => onTaskClick?.(task)}
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {task.is_planned ? (
                    <div className="flex items-center justify-center" title={`Already in route: ${task.planned_route_name}`}>
                      <Lock className="h-4 w-4 text-amber-500" />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(task.uid)}
                      onChange={() => handleSelectTask(task.uid, task.is_planned)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                </td>
                {/* Type (Pickup/RTO) */}
                <td className="px-3 py-2">
                  <Badge
                    variant={task.item_type === 'rto' ? 'danger' : task.item_type === 'cash' ? 'warning' : 'default'}
                    className={cn(
                      "text-[10px]",
                      task.item_type === 'rto'
                        ? "bg-red-100 text-red-700 border-red-200"
                        : task.item_type === 'cash'
                        ? "bg-amber-100 text-amber-700 border-amber-200"
                        : "bg-blue-100 text-blue-700 border-blue-200"
                    )}
                  >
                    {task.item_type === 'rto' ? 'RTO' : task.item_type === 'cash' ? 'Cash' : 'Pickup'}
                  </Badge>
                </td>
                {/* Merchant */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">
                      {task.merchant_name || '-'}
                    </span>
                    {task.requires_dedicated_driver && (
                      <Badge 
                        variant="warning" 
                        className="text-[10px] bg-orange-100 text-orange-700 border-orange-200 whitespace-nowrap"
                        title="This merchant requires a dedicated driver"
                      >
                        <Truck className="h-3 w-3 mr-1" />
                        Dedicated
                      </Badge>
                    )}
                    {/*
                      The merchant's LEGACY preferred window (Mongo
                      merchant_pickup_windows.time_windows) — deliberately NOT the
                      pickup slot, which is the badge below. Labelled "pref" because
                      an unlabelled clock time on a task with no slot reads as one,
                      and the two routinely disagree: a merchant on S5 (00:00-02:00)
                      can still carry a legacy 14:00-17:00 here.

                      It is not decorative. `applySlotWindows` overwrites time_window
                      only for tasks that HAVE a mirror row, so on a task with no slot
                      this window survives into the solver as the time constraint.
                    */}
                    {task.has_pickup_window && task.pickup_window && task.pickup_window.start && task.pickup_window.end && (
                      <Badge
                        variant="default"
                        className="text-[10px] bg-purple-100 text-purple-700 border-purple-200 whitespace-nowrap"
                        title={
                          task.pickup_slot
                            ? `Merchant's preferred pickup time (legacy setting): ${task.pickup_window.start}-${task.pickup_window.end}. This is NOT the pickup slot — this task is on ${task.pickup_slot.slot_code}, and the slot is what routing uses.`
                            : `Merchant's preferred pickup time (legacy setting): ${task.pickup_window.start}-${task.pickup_window.end}. This is NOT a pickup slot, and this task has none. Because there is no slot, this window is what the optimiser gets as the time constraint if the task is routed.`
                        }
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        pref {task.pickup_window.start}-{task.pickup_window.end}
                      </Badge>
                    )}
                    {task.pickup_slot && (
                      <Badge
                        variant="default"
                        className={cn(
                          'text-[10px] whitespace-nowrap',
                          getSlotStateBadgeClasses(task.pickup_slot.slot_state)
                        )}
                        title={`Slot ${task.pickup_slot.slot_code}${
                          task.pickup_slot.preferred_window
                            ? ` · preferred ${task.pickup_slot.preferred_window.start}-${task.pickup_slot.preferred_window.end}`
                            : ''
                        } · ${SLOT_STATE_LABELS[task.pickup_slot.slot_state] ?? task.pickup_slot.slot_state}`}
                      >
                        {task.pickup_slot.slot_code}
                        {task.pickup_slot.preferred_window &&
                          ` ${task.pickup_slot.preferred_window.start}-${task.pickup_slot.preferred_window.end}`}
                      </Badge>
                    )}
                    {/*
                      The merchant's chosen slot, shown ONLY where there is no
                      booked slot to show instead — in practice every RTO, because
                      the slot mirror is keyed by pickup and a return never gets a
                      row. Until now that made every return on this page look like
                      it had no slot at all, while deferRtoToMerchantShift was
                      routing it by this exact value the whole time.

                      Styled apart from the slot badge above (outline, "merchant"
                      prefix) because it is a standing preference, not a booking
                      for today — conflating the two is the mistake that produced
                      a wrong slot-adherence analysis once already.
                    */}
                    {!task.pickup_slot && task.item_type === 'rto' && (
                      <Badge
                        variant="default"
                        className="text-[10px] whitespace-nowrap bg-white text-gray-600 border-gray-300 border-dashed"
                        title={
                          task.merchant_slot_code
                            ? `This merchant's chosen pickup slot is ${task.merchant_slot_code}. Returns are delivered on the shift that owns it — a return has no slot booking of its own.`
                            : 'This merchant has no pickup slot recorded in Core, so any shift may deliver the return. Not an error — it is the default.'
                        }
                      >
                        merchant {task.merchant_slot_code ?? 'any shift'}
                      </Badge>
                    )}
                  </div>
                </td>
                {/* Area */}
                <td className="px-3 py-2">
                  <span className="text-sm text-gray-900">
                    {task.area_name || '-'}
                  </span>
                </td>
                {/* Date */}
                <td className="px-3 py-2">
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {new Date(task.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).replace(/ /g, '\u00A0')}
                  </span>
                </td>
                {/* Status */}
                <td className="px-3 py-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <Badge variant={task.task_status === 'assigned' ? 'success' : 'default'} className="text-[10px]">
                        {task.task_status}
                      </Badge>
                      {/*
                        "Planned" was the lie that hid four days of work: 160 tasks
                        sat on driverless DRAFT routes after the 31/7-3/8 holiday
                        wearing this badge, so nobody looked. A draft with no driver
                        is not planned in any sense that matters — say so, in red.
                      */}
                      {task.is_planned && (
                        <Badge
                          variant={task.routing_status?.code === 'planned_not_dispatched' ? 'danger' : 'warning'}
                          className={
                            task.routing_status?.code === 'planned_not_dispatched'
                              ? 'text-[10px] bg-red-100 text-red-800 border-red-300'
                              : 'text-[10px] bg-amber-100 text-amber-700 border-amber-200'
                          }
                          title={
                            task.routing_status?.code === 'planned_not_dispatched'
                              ? `On ${task.planned_route_name || 'a draft route'} with no driver assigned — ` +
                                'nothing will collect it until someone is assigned.'
                              : `Planned in: ${task.planned_route_name} (${task.planned_route_status})`
                          }
                        >
                          {task.routing_status?.code === 'planned_not_dispatched' ? 'Planned · no driver' : 'Planned'}
                        </Badge>
                      )}
                      {/*
                        How long this has been waiting. A pickup carried over for six
                        days looked identical to one booked twenty minutes ago, which
                        is the other half of why the backlog went unnoticed.
                      */}
                      {(() => {
                        const settled =
                          task.routing_status?.state === 'routed' || task.routing_status?.state === 'done';
                        if (settled || !task.created_at) return null;
                        const ageDays = Math.floor(
                          (Date.now() - new Date(task.created_at).getTime()) / 86_400_000
                        );
                        if (ageDays < 1) return null;
                        return (
                          <Badge
                            variant="danger"
                            className={
                              ageDays >= 3
                                ? 'text-[10px] bg-red-100 text-red-800 border-red-300'
                                : 'text-[10px] bg-amber-100 text-amber-800 border-amber-300'
                            }
                            title={`Booked ${ageDays} day(s) ago and still not collected.`}
                          >
                            {ageDays}d old
                          </Badge>
                        );
                      })()}
                      {/*
                        An officer-removed task is otherwise indistinguishable from
                        one that was never planned — same empty "Planned" column —
                        so nobody can tell it is being held out on purpose.
                      */}
                      {/*
                        The answer to "why isn't this in a route". Gated on `state`
                        alone, NOT on is_planned: the backend returns `routed` only
                        when a route can actually move the task, so a planned-but-
                        undispatched one must still explain itself. Gating on
                        is_planned is what suppressed this badge for all 160 of them.
                        Colour carries the urgency so a manager can scan for red
                        without reading; the sentence is in the title.
                      */}
                      {task.routing_status &&
                        task.routing_status.state !== 'routed' && (
                          <Badge
                            variant={task.routing_status.state === 'blocked' ? 'danger' : 'default'}
                            className={
                              task.routing_status.state === 'blocked'
                                ? 'text-[10px] border-red-300 bg-red-100 text-red-800'
                                : task.routing_status.state === 'waiting'
                                  ? 'text-[10px] border-sky-200 bg-sky-50 text-sky-700'
                                  : task.routing_status.state === 'held'
                                    ? 'text-[10px] border-orange-200 bg-orange-50 text-orange-700'
                                    : 'text-[10px] border-gray-200 bg-gray-50 text-gray-600'
                            }
                            title={task.routing_status.message}
                          >
                            {/* Core has it on a driver, Fleet has no stop (BOXY-561) — not a routing problem. */}
                            {task.routing_status.code === 'on_driver_off_route'
                              ? 'On driver · no route'
                              : /* Core dated it a later day (BOXY-643) — not "the system will route it tonight". */
                                task.routing_status.code === 'deferred_to_next_day'
                              ? 'Moved to tomorrow'
                              : task.routing_status.state === 'blocked'
                              ? 'Not routable'
                              : task.routing_status.state === 'waiting'
                                ? 'Waiting'
                                : task.routing_status.state === 'held'
                                  ? 'Held'
                                  : task.routing_status.state === 'done'
                                    ? 'Finished'
                                    : /*
                                        `unknown` is not one thing. The backend
                                        returns it for a task type the slot ladder
                                        does not evaluate — every RTO and every
                                        cash task — as well as for the genuine
                                        "facts do not explain this" case. Showing
                                        one word for both told an officer that a
                                        perfectly normal return was a mystery.
                                      */
                                      task.routing_status.code === 'not_slot_routed'
                                      ? task.item_type === 'rto'
                                        ? 'With merchant’s shift'
                                        : 'Not slot-routed'
                                      : 'Unclear'}
                          </Badge>
                        )}
                      {task.auto_route_excluded && (
                        <Badge
                          variant="danger"
                          className="text-[10px] bg-rose-100 text-rose-700 border-rose-200"
                          title={
                            `Removed from ${task.auto_route_excluded.removed_from_route || 'a route'}` +
                            `${task.auto_route_excluded.removed_by ? ` by ${task.auto_route_excluded.removed_by}` : ''}` +
                            ` — held out of auto-routing for ${task.auto_route_excluded.operational_date}.` +
                            ' Add it to a route by hand to release it.'
                          }
                        >
                          Removed from route
                        </Badge>
                      )}
                    </div>
                    {task.task_status === 'assigned' && task.assigned_driver_name && (
                      <span className="text-[10px] text-blue-700 font-medium flex items-center gap-1 whitespace-nowrap">
                        <Truck className="h-3 w-3 flex-shrink-0" />
                        {task.assigned_driver_name}
                      </span>
                    )}
                  </div>
                </td>
                {/* Zone */}
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {task.zone_name ? (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(task.zone_name || '');
                      }}
                      className="group inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                      title={`Click to copy: ${task.zone_name}`}
                    >
                      <Badge 
                        variant="default" 
                        className="text-[10px] whitespace-nowrap"
                        style={{ 
                          backgroundColor: task.zone_color ? `${task.zone_color}20` : undefined,
                          color: task.zone_color || undefined,
                          borderColor: task.zone_color || undefined,
                        }}
                      >
                        {task.zone_name.replace(/ /g, '\u00A0')}
                      </Badge>
                      <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </button>
                  ) : (
                    <Badge variant="danger" className="text-[10px] whitespace-nowrap">
                      No&nbsp;Zone
                    </Badge>
                  )}
                </td>
                {/* Route */}
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {task.planned_route_name ? (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(task.planned_route_name || '');
                      }}
                      className="group inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                      title={
                        task.routing_status?.code === 'planned_not_dispatched'
                          ? `On ${task.planned_route_name} — no driver assigned yet, so nothing will collect it. Click to copy.`
                          : `On ${task.planned_route_name} (${task.planned_route_status}). Click to copy.`
                      }
                    >
                      {/*
                        Red for a route with nobody driving it. A draft with no
                        driver is not planned in any sense that matters — 160 tasks
                        sat on one for four days because nothing on the row said so.
                      */}
                      <Badge
                        variant={
                          task.routing_status?.code === 'planned_not_dispatched' ? 'danger' : 'default'
                        }
                        className={cn(
                          'text-[10px] whitespace-nowrap',
                          task.routing_status?.code === 'planned_not_dispatched'
                            ? 'bg-red-100 text-red-800 border-red-300'
                            : 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {task.planned_route_name.replace(/ /g, '\u00A0')}
                      </Badge>
                      <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </button>
                  ) : (
                    // A dash, not an empty cell: "not on a route" is an answer, and
                    // blank reads as "we don't know".
                    <span className="text-[11px] text-gray-500" title="Not on a route yet">
                      &mdash;
                    </span>
                  )}
                </td>
                {/* Orders */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Badge variant="default" className="text-[10px] bg-gray-100 text-gray-700">
                      {task.number_of_orders}
                    </Badge>
                    <CoolingBadge count={task.cooling_count} />
                  </div>
                </td>
                {/* UID */}
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(task.uid);
                    }}
                    className="group flex items-center gap-1 text-[10px] text-gray-500 font-mono hover:text-blue-600 transition-colors whitespace-nowrap"
                    title={`Click to copy: ${task.uid}`}
                  >
                    <span>{task.uid.slice(-8)}</span>
                    <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                </td>
                {/* Platform */}
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(task.platform_code);
                    }}
                    className="group flex items-center gap-1 text-[10px] text-gray-600 font-mono hover:text-blue-600 transition-colors whitespace-nowrap"
                    title={`Click to copy: ${task.platform_code}`}
                  >
                    <span>{task.platform_code.replace(/-/g, '\u2011')}</span>
                    <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                </td>
                {/* Province */}
                <td className="px-3 py-2">
                  <span className="text-sm text-gray-900">
                    {task.province || '-'}
                  </span>
                </td>
                {/* Actions */}
                {(onMarkTaskAsDone || onUnassignTask) && (
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {onMarkTaskAsDone && can('tasks.mark_done') && task.task_status !== 'done' && task.task_status !== 'cancelled' && (
                        <button
                          onClick={() => onMarkTaskAsDone(task.uid, task.merchant_name || undefined)}
                          disabled={isMarkingDone === task.uid}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mark task as done"
                        >
                          {isMarkingDone === task.uid ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Marking...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3.5 w-3.5" />
                              Mark Done
                            </>
                          )}
                        </button>
                      )}
                      {onUnassignTask && can('routes.edit') && task.is_planned && ASSIGNED_TASK_STATUSES.includes(task.task_status) && (
                        <button
                          onClick={() => onUnassignTask(task.uid, task.merchant_name || undefined)}
                          disabled={isUnassigning === task.uid}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Unassign task and return it to the pool for re-planning"
                        >
                          {isUnassigning === task.uid ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Unassigning...
                            </>
                          ) : (
                            <>
                              <UserX className="h-3.5 w-3.5" />
                              Unassign
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selection summary */}
      {selectedTasks.size > 0 && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 flex items-center justify-between">
          <span className="text-sm text-blue-700">
            {selectedTasks.size} task{selectedTasks.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => onSelectionChange(new Set())}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  );
}
